import React, { useContext, useEffect, useRef } from 'react';
import { Box, Copy, FloaterTooltipMarkdown, H6, Icon, Link, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import Context from './service/Context';
import SubCategoriesDropdown from './shared/SubCategoriesDropdown';
import gtmForGenericCtaLinkClick from '../../core/gtm/gtmForGenericCtaLinkClick';
import isSSR from '../../utils/isSSR';
import themeStyles from './ViewMoreAccessoriesPanel.styles';
import { selectModelNameByModelKey } from '../../core/reducers/vehicles';
import { setActiveProgressBarItem, setCurrentViewMoreModalId } from '../../core/reducers/ui';
import { useAppDispatch, useAppSelector } from '../../core/utils';
const MainContainer = themeStyles.apply(Box, 'MainContainer');
const AccessoryCardContainer = themeStyles.apply(Box, 'AccessoryCardContainer');
const BackCta = themeStyles.apply(Link, 'BackCta');
const BackIcon = themeStyles.apply(Icon, 'BackIcon');
const HeaderContainer = themeStyles.apply(Box, 'HeaderContainer');
const ViewMoreAccessoriesPanel = ({ label, setSelectedSubCategory, selectedSubCategory, defaultSubCategory, categoryOptions, isProtection, children, gtmPayload, componentName, customizeSection, isFeaturedSubCategorySelected, onClose, }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const backCtaRef = useRef(null);
    const hasPlusIcon = isProtection;
    const { backCtaColor, backIconColor, configurePreviousPosition, setConfigurePreviousPosition } = useContext(Context);
    const modelName = useAppSelector(selectModelNameByModelKey);
    const restoreDefaultSubcategory = () => {
        if (!defaultSubCategory)
            return;
        setSelectedSubCategory === null || setSelectedSubCategory === void 0 ? void 0 : setSelectedSubCategory(defaultSubCategory);
    };
    useEffect(() => {
        if (backCtaRef.current) {
            backCtaRef.current.scrollIntoView({ block: 'center' });
        }
        if (customizeSection)
            dispatch(setActiveProgressBarItem(customizeSection));
        return () => {
            if (selectedSubCategory === defaultSubCategory)
                return;
            restoreDefaultSubcategory();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        // If the selectedSubCategory is empty, it won't appear in the options list so the default category should be set
        if (!(categoryOptions === null || categoryOptions === void 0 ? void 0 : categoryOptions.some(({ value }) => value === selectedSubCategory))) {
            restoreDefaultSubcategory();
        }
    }, [selectedSubCategory]);
    useEffect(() => {
        if (!isSSR()) {
            window.scrollTo(0, configurePreviousPosition);
        }
    }, [configurePreviousPosition]);
    const accessoriesTooltipText = t('Pages.Bap.accessoriesTooltipText');
    return (React.createElement(MainContainer, { "data-testid": "cy-view-more-accessories-panel" },
        React.createElement(BackCta, { ref: backCtaRef, as: "button", styling: "tertiary", onClick: () => {
                restoreDefaultSubcategory();
                setConfigurePreviousPosition === null || setConfigurePreviousPosition === void 0 ? void 0 : setConfigurePreviousPosition(0);
                if (setCurrentViewMoreModalId) {
                    dispatch(setCurrentViewMoreModalId(''));
                }
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }, "data-testid": "view-more-accessories-back-cta", backCtaColor: backCtaColor },
            React.createElement(BackIcon, { iconColor: backIconColor, name: "arrowLeft" }),
            t('Pages.Bap.backButton')),
        React.createElement(HeaderContainer, null,
            React.createElement(H6, { "data-testid": "view-more-accessories-label" },
                label,
                React.createElement(FloaterTooltipMarkdown, { as: "span", content: accessoriesTooltipText, ariaLabel: label, ml: "xs", styling: "light", "data-testid": "view-more-accessories-tooltip", actionOnVisibilityChange: () => gtmForGenericCtaLinkClick({
                        ...gtmPayload,
                        ctaType: 'click',
                        eventName: 'cta_click',
                        linkText: accessoriesTooltipText,
                        componentType: componentName,
                        customizeSection,
                    }) }, ({ active }) => React.createElement(Icon, { name: "information", filled: active }))),
            hasPlusIcon && React.createElement(Icon, { name: "plusLogo" })),
        React.createElement(Optional, { when: isFeaturedSubCategorySelected },
            React.createElement(Copy, { size: "regular", my: "m", "data-testid": "cy-model-name-protection" }, t('Pages.Bap.protectionBlurbText', { modelName }))),
        React.createElement(SubCategoriesDropdown, { selectedValue: selectedSubCategory, defaultValue: defaultSubCategory, setSelectedSubCategory: setSelectedSubCategory, options: categoryOptions }),
        React.createElement(AccessoryCardContainer, { isProtection: isProtection }, children)));
};
export default ViewMoreAccessoriesPanel;
